import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram as IgIcon, Linkedin as LiIcon } from "lucide-react";
import { socials, contactPhone } from "../data/categoriesData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Food", "Products", "Events", "Ad Films", "Other"];

// WhatsApp: using a direct wa.me link. SVG icon inline for minimal deps.
const WaIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04.06C5.52.06.2 5.37.2 11.86c0 2.08.55 4.11 1.58 5.9L0 24l6.4-1.7a11.77 11.77 0 0 0 5.63 1.44h.01c6.52 0 11.84-5.31 11.84-11.8 0-3.15-1.23-6.11-3.36-8.46ZM12.04 21.7h-.01a9.81 9.81 0 0 1-4.99-1.36l-.36-.21-3.8 1 1.02-3.69-.23-.38a9.72 9.72 0 0 1-1.5-5.2c0-5.4 4.43-9.8 9.87-9.8 2.64 0 5.12 1.02 6.98 2.87a9.71 9.71 0 0 1 2.88 6.95c0 5.4-4.43 9.82-9.86 9.82Zm5.4-7.35c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47s1.07 2.87 1.22 3.07c.15.2 2.1 3.21 5.09 4.5.71.3 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z"/>
  </svg>
);

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        project_type: form.project_type || null,
        message: form.message,
      });
      setSubmitted(true);
      toast.success("Thank you. We'll be in touch within 48 hours.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-black border-t border-white/5 py-20 sm:py-32 lg:py-44"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <h2 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
              Let's make
              <br />
              <em className="italic font-medium">something</em>
              <br />
              worth keeping.
            </h2>

            <div className="mt-10 space-y-6 text-white/70">
              <div>
                <div className="eyebrow mb-2">Studio</div>
                <p className="font-light leading-relaxed">
                  Momenta Studios
                  <br />
                  Mumbai, Maharashtra, India
                </p>
              </div>
              <div>
                <div className="eyebrow mb-2">Reach</div>
                <a
                  href="mailto:hello@momentastudios.in"
                  data-testid="contact-email-link"
                  className="font-serif italic text-xl sm:text-2xl text-white link-underline break-all"
                >
                  hello@momentastudios.in
                </a>
                <div className="mt-2 font-light text-sm text-white/60" data-testid="contact-phone">
                  <a href={`tel:${contactPhone.replace(/\s+/g, "")}`} className="hover:text-white transition-colors">{contactPhone}</a>
                </div>
              </div>

              {/* Social icons */}
              <div>
                <div className="eyebrow mb-3">Follow</div>
                <div
                  data-testid="contact-socials"
                  className="flex items-center gap-3"
                >
                  <a
                    href={socials.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="social-instagram"
                    aria-label="Instagram"
                    className="w-11 h-11 border border-white/25 hover:border-white hover:bg-white hover:text-black text-white flex items-center justify-center transition-colors"
                  >
                    <IgIcon size={18} strokeWidth={1.5} />
                  </a>
                  <a
                    href={socials.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="social-whatsapp"
                    aria-label="WhatsApp"
                    className="w-11 h-11 border border-white/25 hover:border-white hover:bg-white hover:text-black text-white flex items-center justify-center transition-colors"
                  >
                    <WaIcon className="w-[18px] h-[18px]" />
                  </a>
                  <a
                    href={socials.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="social-linkedin"
                    aria-label="LinkedIn"
                    className="w-11 h-11 border border-white/25 hover:border-white hover:bg-white hover:text-black text-white flex items-center justify-center transition-colors"
                  >
                    <LiIcon size={18} strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-white/20 p-8 sm:p-14"
              >
                <h3 className="font-serif text-white text-3xl sm:text-4xl font-light italic leading-tight">
                  Thank you. Your note is on our table.
                </h3>
                <p className="mt-5 text-white/60 text-sm leading-relaxed">
                  We read every message the morning it arrives and respond
                  within two working days.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                  className="mt-8 border border-white/30 hover:border-white text-white px-5 py-3 text-[11px] tracking-[0.3em] uppercase transition-colors"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="space-y-8"
              >
                <div>
                  <label className="eyebrow block mb-1">Name</label>
                  <input
                    data-testid="contact-input-name"
                    className="input-underline"
                    type="text"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Your full name"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="eyebrow block mb-1">Email</label>
                    <input
                      data-testid="contact-input-email"
                      className="input-underline"
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      placeholder="you@studio.com"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-1">Phone</label>
                    <input
                      data-testid="contact-input-phone"
                      className="input-underline"
                      type="tel"
                      value={form.phone}
                      onChange={onChange("phone")}
                      placeholder="+91"
                    />
                  </div>
                </div>

                <div>
                  <label className="eyebrow block mb-3">Project Type</label>
                  <div className="flex flex-wrap gap-2">
                    {PROJECT_TYPES.map((p) => (
                      <button
                        type="button"
                        key={p}
                        onClick={() => setForm({ ...form, project_type: p })}
                        data-testid={`contact-project-${p.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`px-4 py-2 text-[11px] tracking-[0.25em] uppercase border transition-colors ${
                          form.project_type === p
                            ? "bg-white text-black border-white"
                            : "border-white/20 text-white/70 hover:border-white/50 hover:text-white"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="eyebrow block mb-1">
                    Tell us about it
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    className="input-underline"
                    rows={4}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Brief, timeline, references, dreams."
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="group inline-flex items-center gap-6 bg-white text-black hover:bg-white/85 disabled:opacity-60 disabled:cursor-not-allowed px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors"
                  >
                    <span>{loading ? "Sending" : "Send Enquiry"}</span>
                    <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
                      →
                    </span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
